import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import heroImage from '../../assets/images_of_victoria/IMG_2250.jpg';
import imageOne from '../../assets/images_of_victoria/IMG_2253.jpg';
import imageTwo from '../../assets/images_of_victoria/IMG_2264 (1).jpg';
import imageThree from '../../assets/images_of_victoria/IMG_2280.jpg';
import imageFour from '../../assets/images_of_victoria/IMG_2296.jpg';

const galleryHighlights = [
  {
    label: 'Joy',
    description: 'The gallery helps visitors see the delight, personality, and warmth behind the mission.',
  },
  {
    label: 'Presence',
    description: "Real photos make the work of Victoria's Place feel lived-in, visible, and trustworthy.",
  },
  {
    label: 'Memory',
    description: 'Events, milestones, and everyday moments deserve a place where they can be held and revisited.',
  },
];

const galleryMoments = [
  {
    src: imageOne,
    alt: 'Victoria smiling outside in a colorful dress',
    title: 'Joy should never feel hidden.',
    description: "The gallery gives families and supporters a way to see the brightness of Victoria's Place for themselves.",
    className: 'gallery-editorial-card gallery-editorial-card--tall',
  },
  {
    src: imageTwo,
    alt: 'Victoria sitting outside with wings and flowers',
    title: 'Celebration belongs in the story too.',
    description: 'Photos of milestones, playful moments, and growth make the mission easier to understand at a glance.',
    className: 'gallery-editorial-card',
  },
  {
    src: imageThree,
    alt: 'Victoria standing outside in a pink dress',
    title: 'A gallery can feel personal, not generic.',
    description: 'This page is meant to show the life, care, and encouragement behind the organization.',
    className: 'gallery-editorial-card',
  },
  {
    src: imageFour,
    alt: 'Victoria standing barefoot on the grass in a white dress',
    title: 'Families remember what they can see.',
    description: 'As more events, programs, and community moments are added, this becomes a record of belonging.',
    className: 'gallery-editorial-card gallery-editorial-card--wide',
  },
];

export default function Gallery() {
  return (
    <div className="gallery-page">
      <section className="gallery-showcase-hero gallery-bleed">
        <div className="gallery-showcase-media">
          <img src={heroImage} alt="Victoria outdoors, smiling and waving" />
        </div>

        <motion.div
          className="gallery-showcase-inner"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
        >
          <div className="gallery-showcase-copy">
            <span className="eyebrow gallery-showcase-eyebrow">Gallery</span>
            <p className="gallery-showcase-kicker">A visual home for joy, milestones, and the life of the mission</p>
            <h1>Photos help people feel the warmth of Victoria&apos;s Place right away.</h1>
            <p className="gallery-showcase-lead">
              The gallery exists so families, supporters, and future partners can see the joy, personality, and
              real-life care behind the work instead of having to imagine it from text alone.
            </p>

            <div className="gallery-showcase-actions">
              <Link to="/events" className="button-primary tap-target">
                Explore events
              </Link>
              <Link to="/contact" className="button-secondary tap-target">
                Share a memory
              </Link>
            </div>
          </div>
        </motion.div>
      </section>

      <div className="page-shell gallery-shell">
        <motion.section
          className="gallery-highlight-row"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.24 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
        >
          {galleryHighlights.map((item) => (
            <article key={item.label} className="gallery-highlight">
              <p className="home-section-label">{item.label}</p>
              <p>{item.description}</p>
            </article>
          ))}
        </motion.section>

        <section className="gallery-story-grid">
          <motion.figure
            className="gallery-story-photo"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <img src={imageThree} alt="Victoria in a bright pink dress outdoors" />
          </motion.figure>

          <motion.div
            className="gallery-story-copy"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.24 }}
            transition={{ duration: 0.48, ease: 'easeOut' }}
          >
            <p className="home-section-label">Why A Gallery Matters</p>
            <h2>Seeing the children, the joy, and the moments of growth makes the mission easier to trust.</h2>
            <p>
              Victoria&apos;s Place is rooted in faith, family, and community. A gallery gives those values somewhere to
              live visually. It helps the site feel personal, specific, and grounded in real people instead of
              abstract promises.
            </p>
            <p>
              Over time, this page can become the home for event photos, community highlights, and the kinds of
              everyday moments that remind families they are seen and welcomed here.
            </p>
          </motion.div>
        </section>

        <section className="gallery-editorial-section">
          <div className="gallery-editorial-heading">
            <p className="home-section-label">Moments</p>
            <h2>Real images should carry the emotional proof of the site.</h2>
          </div>

          <div className="gallery-editorial-grid">
            {galleryMoments.map((item, index) => (
              <motion.figure
                key={item.alt}
                className={item.className}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.16 }}
                transition={{ duration: 0.45, delay: index * 0.04, ease: 'easeOut' }}
              >
                <div className="gallery-editorial-image">
                  <img src={item.src} alt={item.alt} />
                </div>
                <figcaption className="gallery-editorial-caption">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </section>

        <motion.section
          className="gallery-final-band"
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.24 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <div className="gallery-final-copy">
            <p className="home-section-label home-section-label--light">Keep The Story Visible</p>
            <h2>As the site grows, this page can become a living record of events, families, and encouragement.</h2>
            <p>
              If you want to share photos, celebrate a moment, or help document the work of Victoria&apos;s Place, reach
              out and we can build this page out over time.
            </p>
          </div>

          <div className="gallery-final-actions">
            <Link to="/contact" className="button-secondary tap-target">
              Contact Victoria&apos;s Place
            </Link>
          </div>
        </motion.section>
      </div>
    </div>
  );
}
